import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  BarChart3, ArrowLeft, ArrowUp, ArrowDown, Minus, Receipt, Wallet,
  AlertTriangle, ChevronLeft, ChevronRight, TrendingUp, Loader2,
} from "lucide-react";
import api from "@/lib/api";

function fmtChf(n) {
  if (n == null) return "—";
  return n.toLocaleString("fr-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
const MOIS_LONG = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];
function fmtPeriode(s) {
  if (!s) return "";
  const [y, m] = s.split("-").map(Number);
  return `${MOIS_LONG[m - 1]} ${y}`;
}
function moisPrec(s) {
  const [y, m] = s.split("-").map(Number);
  if (m === 1) return `${y - 1}-12`;
  return `${y}-${String(m - 1).padStart(2, "0")}`;
}
function moisSuiv(s) {
  const [y, m] = s.split("-").map(Number);
  if (m === 12) return `${y + 1}-01`;
  return `${y}-${String(m + 1).padStart(2, "0")}`;
}
function nowPeriode() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

// Pour les charges : baisse = bon (vert), hausse = mauvais (rouge)
function variationColor(sens) {
  if (sens === "baisse") return "text-emerald-700 bg-emerald-50 border-emerald-200";
  if (sens === "hausse") return "text-orange-700 bg-orange-50 border-orange-200";
  return "text-gray-600 bg-gray-50 border-gray-200";
}
function VariationBadge({ v, suffix = "%" }) {
  if (!v) return null;
  const Icon = v.sens === "baisse" ? ArrowDown : v.sens === "hausse" ? ArrowUp : Minus;
  return (
    <Badge variant="outline" className={`text-[10px] gap-0.5 ${variationColor(v.sens)}`}>
      <Icon className="w-3 h-3" />
      {Math.abs(v.pct)}{suffix}
      <span className="text-[9px] opacity-70 ml-1">
        ({v.delta > 0 ? "+" : ""}{fmtChf(v.delta)})
      </span>
    </Badge>
  );
}

export default function AchatsStatistiques() {
  const navigate = useNavigate();
  const [periode, setPeriode] = useState(nowPeriode());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/achats/statistiques?periode=${periode}`);
      setData(data);
    } catch {
      toast.error("Erreur chargement stats");
    } finally { setLoading(false); }
  }, [periode]);

  useEffect(() => { reload(); }, [reload]);

  const isCurrentMonth = useMemo(() => periode === nowPeriode(), [periode]);

  if (loading || !data) {
    return <div className="p-8 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  }

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="page-achats-stats">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Retour
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 flex items-center gap-2">
              <BarChart3 className="w-7 h-7 text-purple-600" />
              Statistiques achats
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Factures fournisseurs + Dépenses rapides — vue unifiée mensuelle
            </p>
          </div>
        </div>
        {/* Navigation mois */}
        <div className="flex items-center gap-2 bg-white rounded-lg border border-gray-200 px-2 py-1">
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
            onClick={() => setPeriode(moisPrec(periode))}
            data-testid="stats-mois-prec">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm font-semibold text-gray-900 min-w-[140px] text-center" data-testid="stats-periode">
            {fmtPeriode(periode)}
          </span>
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
            disabled={isCurrentMonth}
            onClick={() => setPeriode(moisSuiv(periode))}
            data-testid="stats-mois-suiv">
            <ChevronRight className="w-4 h-4" />
          </Button>
          {!isCurrentMonth && (
            <Button size="sm" variant="ghost" className="text-xs h-7 text-blue-600"
              onClick={() => setPeriode(nowPeriode())}
              data-testid="stats-aujourd-hui">
              Aujourd'hui
            </Button>
          )}
        </div>
      </div>

      {/* Section 1 : Factures à payer */}
      <Card className="p-5 mb-4" data-testid="card-factures-a-payer">
        <h2 className="text-sm uppercase tracking-wider font-semibold text-gray-500 mb-3 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-orange-500" />
          Factures à payer (actuellement)
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <StatBlock
            label="Factures"
            value={data.factures_a_payer.nombre}
            valueClass="text-3xl font-bold text-gray-900"
            sub="à régler"
          />
          <StatBlock
            label="Montant total"
            value={`${fmtChf(data.factures_a_payer.montant_total)} CHF`}
            valueClass="text-3xl font-bold text-gray-900"
            sub="restant à payer"
          />
          <StatBlock
            label="Dont en retard"
            value={data.factures_a_payer.en_retard_nombre}
            valueClass={`text-3xl font-bold ${data.factures_a_payer.en_retard_nombre > 0 ? "text-red-700" : "text-emerald-700"}`}
            sub={`${fmtChf(data.factures_a_payer.en_retard_montant)} CHF`}
            highlight={data.factures_a_payer.en_retard_nombre > 0 ? "danger" : null}
          />
        </div>
      </Card>

      {/* Section 2 : Comparatif mensuel */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <Card className="p-5" data-testid="card-factures-mois">
          <h2 className="text-sm uppercase tracking-wider font-semibold text-blue-700 mb-3 flex items-center gap-2">
            <Receipt className="w-4 h-4" />
            Factures fournisseurs · {fmtPeriode(periode)}
          </h2>
          <div className="space-y-3">
            <MetricRow label="Nombre"
              valueMois={data.factures_mois.nombre}
              valuePrec={data.factures_mois_precedent.nombre}
              isPct={false} />
            <MetricRow label="Total TTC"
              valueMois={`${fmtChf(data.factures_mois.ttc)} CHF`}
              valuePrec={`${fmtChf(data.factures_mois_precedent.ttc)} CHF`}
              variation={data.comparatif.factures_ttc} />
            <MetricRow label="TVA récupérable"
              valueMois={`${fmtChf(data.factures_mois.tva)} CHF`}
              valuePrec={`${fmtChf(data.factures_mois_precedent.tva)} CHF`}
              variation={data.comparatif.factures_tva}
              accentClass="text-blue-700 font-semibold" />
          </div>
        </Card>

        <Card className="p-5" data-testid="card-depenses-mois">
          <h2 className="text-sm uppercase tracking-wider font-semibold text-orange-700 mb-3 flex items-center gap-2">
            <Wallet className="w-4 h-4" />
            Dépenses rapides · {fmtPeriode(periode)}
          </h2>
          <div className="space-y-3">
            <MetricRow label="Nombre"
              valueMois={data.depenses_mois.nombre}
              valuePrec={data.depenses_mois_precedent.nombre}
              isPct={false} />
            <MetricRow label="Total TTC"
              valueMois={`${fmtChf(data.depenses_mois.ttc)} CHF`}
              valuePrec={`${fmtChf(data.depenses_mois_precedent.ttc)} CHF`}
              variation={data.comparatif.depenses_ttc} />
            <MetricRow label="TVA récupérable"
              valueMois={`${fmtChf(data.depenses_mois.tva)} CHF`}
              valuePrec={`${fmtChf(data.depenses_mois_precedent.tva)} CHF`}
              variation={data.comparatif.depenses_tva}
              accentClass="text-orange-700 font-semibold" />
          </div>
        </Card>
      </div>

      {/* Section 3 : Total achats/dépenses du mois */}
      <Card className="p-5 bg-gradient-to-br from-purple-50 to-white border-purple-200" data-testid="card-total-mois">
        <h2 className="text-sm uppercase tracking-wider font-semibold text-purple-700 mb-3 flex items-center gap-2">
          <TrendingUp className="w-4 h-4" />
          Total achats + dépenses · {fmtPeriode(periode)}
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <div>
            <div className="text-xs text-gray-500">Nombre total de pièces</div>
            <div className="text-3xl font-bold text-gray-900 mt-1">
              {data.total_mois.nombre}
            </div>
            <div className="text-[11px] text-gray-400 mt-0.5">
              vs {data.total_mois_precedent.nombre} le mois dernier
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Total TTC</div>
            <div className="text-3xl font-bold text-purple-700 mt-1">
              {fmtChf(data.total_mois.ttc)} <span className="text-base text-gray-500">CHF</span>
            </div>
            <div className="mt-1">
              <VariationBadge v={data.comparatif.total_ttc} />
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">TVA récupérable totale</div>
            <div className="text-3xl font-bold text-blue-700 mt-1">
              {fmtChf(data.total_mois.tva)} <span className="text-base text-gray-500">CHF</span>
            </div>
            <div className="mt-1">
              <VariationBadge v={data.comparatif.total_tva} />
            </div>
          </div>
        </div>
        <p className="text-[11px] text-gray-500 mt-4 italic">
          ℹ️ Basé uniquement sur les factures validées et dépenses comptabilisées
          (brouillons et rejetées exclus). Les charges réparties analytiquement
          sont ventilées sur les mois concernés.
        </p>
      </Card>
    </div>
  );
}

function StatBlock({ label, value, valueClass = "text-2xl font-bold", sub, highlight }) {
  const bg = highlight === "danger" ? "bg-red-50 border-red-100" : "bg-gray-50 border-gray-100";
  return (
    <div className={`rounded-lg border p-3 ${bg}`}>
      <div className="text-xs text-gray-500 uppercase tracking-wider">{label}</div>
      <div className={`${valueClass} mt-1`}>{value}</div>
      {sub && <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function MetricRow({ label, valueMois, valuePrec, variation, accentClass = "", isPct = true }) {
  return (
    <div className="flex items-center justify-between">
      <div className="text-sm text-gray-600">{label}</div>
      <div className="flex items-center gap-3">
        <div className={`text-lg font-bold ${accentClass || "text-gray-900"}`}>{valueMois}</div>
        {variation && isPct && <VariationBadge v={variation} />}
        <div className="text-[11px] text-gray-400 hidden sm:block min-w-[100px] text-right">
          (était {valuePrec})
        </div>
      </div>
    </div>
  );
}
